import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import { useModels } from '../context/ModelContext';
import { useProjects } from '../context/ProjectContext';
import api, { setAuthToken, ChatRequest, getConversation, createConversation, getModelOptions, ModelOption as ApiModelOption, getAgent, getConnectionStatus, getConnectionAuthorizeUrl, SharePointSite } from '../services/api';
import ModelSelector, { ModelOption } from '../components/ModelSelector';
import ChatContainer from '../components/ChatContainer';
import ChatInput from '../components/ChatInput';
import WelcomeScreen from '../components/WelcomeScreen';
import ConversationSidebar, { ConversationSummary } from '../components/ConversationSidebar';
import Header from '../components/Header';
import ConnectAccountGate from '../components/ConnectAccountGate';
import { Message, AttachedFile } from '../components/ChatMessage';
import { UploadedFile, MAX_IMAGE_FILES } from '../components/FileUpload';
import { useStreamingChat } from '../hooks/useStreamingChat';
import type { StreamSnapshot } from '../hooks/useStreamingChat';

const HomePage: React.FC = () => {
  const { user, getAccessToken, logout } = useAuth();
  const { theme, colors, toggleTheme } = useTheme();
  const { isSidebarOpen, toggleSidebar, setSidebarOpen } = useSidebar();
  const { activeProjectId } = useProjects();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChatStarted, setIsChatStarted] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(undefined);
  const [conversationRefreshTrigger, setConversationRefreshTrigger] = useState<number>(0);
  const [webSearchMode, setWebSearchMode] = useState<'auto' | 'on' | 'off'>('off');
  const [searchDomains, setSearchDomains] = useState<string[]>([]);
  const [isSharePointEnabled, setIsSharePointEnabled] = useState<boolean>(false);
  const [sharePointSites, setSharePointSites] = useState<SharePointSite[]>([]);
  const [isImageGenerationEnabled, setIsImageGenerationEnabled] = useState<boolean>(false);
  const [latestGeneratedImageBlobName, setLatestGeneratedImageBlobName] = useState<string | null>(null);
  const [isDeepResearchEnabled, setIsDeepResearchEnabled] = useState<boolean>(false);
  const [deepResearchSessionId, setDeepResearchSessionId] = useState<string | null>(null);
  const [isInDeepResearch, setIsInDeepResearch] = useState<boolean>(false);
  const [hasEverHadFile, setHasEverHadFile] = useState<boolean>(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState<boolean>(false);
  const [isAnalyzingFiles, setIsAnalyzingFiles] = useState<boolean>(false);
  const [autoSendMessage, setAutoSendMessage] = useState<string | null>(null);
  const [newConversation, setNewConversation] = useState<ConversationSummary | undefined>(undefined);
  const [currentApplication, setCurrentApplication] = useState<{
    id: string;
    title: string;
    customPrompt: string;
    defaultModelProvider?: string;
    defaultModel?: string;
    defaultModelApiVersion?: string;
  } | null>(null);
  const [isNewApplicationConversation, setIsNewApplicationConversation] = useState<boolean>(false);
  const [currentAgent, setCurrentAgent] = useState<{
    id: string;
    title: string;
    description: string;
    img_url: string;
    required_connection?: string;
  } | null>(null);

  // Connection gate for agents that require an external account (per-user OAuth).
  // null = unknown/checking; the gate is shown when required_connection is set and
  // the user is not connected.
  const [connectionChecked, setConnectionChecked] = useState<boolean>(true);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  
  // Get model options from context instead of fetching directly
  const {
    modelOptions,
    selectedModelIndex,
    setSelectedModelIndex,
    isLoading: isLoadingModels,
    error: modelOptionsError,
    quota,
    refreshQuota,
  } = useModels();
  const { streamChat, abort: abortStream } = useStreamingChat();

  // Memoized filtered messages to avoid filtering on every render
  const displayMessages = useMemo(() => {
    if (currentApplication && messages.length > 0) {
      // Filter out application prompts (first user message that looks like an app prompt)
      return messages.filter((msg, index) => {
        if (msg.role === 'user' && index === 0) {
          const isApplicationPrompt = msg.content.includes('Provide a general and then detailed meeting summary') ||
                                    msg.content.length > 200; // Application prompts are typically long
          return !isApplicationPrompt; // Hide application prompts
        }
        return true; // Keep all other messages
      });
    }
    return messages;
  }, [messages, currentApplication]);

  // Load conversation from API
  const loadConversation = async (conversationId: string) => {
    setIsLoadingConversation(true);
    try {
      const token = await getAccessToken();
      setAuthToken(token || '');
      
      const conversation = await getConversation(conversationId);
      
      // Check if this is an application conversation
      if (conversation.is_application && conversation.application_id) {
        setCurrentApplication({
          id: conversation.application_id,
          title: '', // We don't have the title here, but we track the ID
          customPrompt: '',
          defaultModelProvider: undefined,
          defaultModel: undefined,
          defaultModelApiVersion: undefined
        });
        setCurrentAgent(null);
      } else if (conversation.agent_id) {
        // This is an agent conversation - fetch agent details
        setCurrentApplication(null);
        try {
          const agent = await getAgent(conversation.agent_id);
          setCurrentAgent({
            id: agent.id,
            title: agent.title,
            description: agent.description,
            img_url: agent.img_url,
            required_connection: agent.required_connection
          });
        } catch (err) {
          console.error('Error loading agent:', err);
          setCurrentAgent({ id: conversation.agent_id, title: '', description: '', img_url: '' });
        }
      } else {
        setCurrentApplication(null);
        setCurrentAgent(null);
      }

      // When loading an existing conversation, don't hide messages
      setIsNewApplicationConversation(false);

      // Transform API messages to local Message format
      const transformedMessages: Message[] = conversation.messages.map(msg => {
        let messageContent = msg.content;
        let filesInfo: AttachedFile[] = [];

        if (msg.files && msg.files.length > 0) {
          filesInfo = msg.files.map((f: any) => ({
            name: f.filename,
            type: f.content_type,
            size: f.size,
            download_url: `/api/chat/download/${f.blob_name.replace(/\//g, '%2F')}`
          }));
        }

        return {
          role: msg.role as 'user' | 'assistant',
          content: messageContent,
          ...(filesInfo.length > 0 && { files: filesInfo }),
          ...(msg.parts && { parts: (msg.parts as any[]).filter((p: any) => p.type !== 'file_processing') })
        };
      });

      setMessages(transformedMessages);
      setCurrentConversationId(conversationId);
      setIsChatStarted(transformedMessages.length > 0);

      // Check if any messages in the conversation have files
      const hasFiles = transformedMessages.some(msg => msg.files && msg.files.length > 0);
      setHasEverHadFile(hasFiles);

      // Restore latest generated image blob name for image editing continuity
      let lastImageBlobName: string | null = null;
      for (const msg of transformedMessages) {
        if (msg.parts) {
          for (const part of msg.parts) {
            if (part.type === 'image_generation' && 'images' in part && part.images?.[0]?.blobName) {
              lastImageBlobName = part.images[0].blobName;
            }
          }
        }
      }
      setLatestGeneratedImageBlobName(lastImageBlobName);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  // Start new conversation
  const startNewConversation = async (initialMessage?: string, keepAgent: boolean = false) => {
    try {
      setMessages([]);
      setCurrentConversationId(undefined);
      setIsChatStarted(false);
      setInputMessage('');
      setUploadedFiles([]);
      setHasEverHadFile(false);
      setIsInDeepResearch(false);
      setDeepResearchSessionId(null);
      setLatestGeneratedImageBlobName(null);
      setWebSearchMode('off');
      setSearchDomains([]);
      setIsImageGenerationEnabled(false);
      setIsDeepResearchEnabled(false);
      setIsSharePointEnabled(false);
      setSharePointSites([]);

      // Clear current application/agent if not providing initial message (regular new conversation)
      // and not explicitly keeping the agent
      if (!initialMessage && !keepAgent) {
        setCurrentApplication(null);
        setCurrentAgent(null);
        setIsNewApplicationConversation(false);
      } else if (initialMessage) {
        // This is a new application conversation
        setIsNewApplicationConversation(true);
      }

      // If initial message provided, set it up for auto-sending
      if (initialMessage) {
        setAutoSendMessage(initialMessage);
      }
    } catch (error) {
      console.error('Error starting new conversation:', error);
    }
  };
  
  // Helper function to format relative time (like in ConversationSidebar)
  const formatRelativeTime = (dateString: string): string => {
    return 'Just now'; // Simplest implementation - a newly created conversation will always be "Just now"
  };

  // Create conversation when first message is sent
  const createConversationFromMessage = async (firstMessage: string): Promise<string | undefined> => {
    try {
      const token = await getAccessToken();
      setAuthToken(token || '');

      // Generate a title from the first message (first 50 chars)
      const title = firstMessage.length > 50 ? firstMessage.substring(0, 47) + '...' : firstMessage;

      // Check if this is an application conversation
      const isApplication = currentApplication !== null;
      const applicationId = currentApplication?.id;
      const agentId = currentAgent?.id;

      const conversation = await createConversation(title, isApplication, applicationId, agentId, activeProjectId ?? undefined);
      
      // Create a conversation summary object to pass to the sidebar
      const now = new Date().toISOString();
      const newConvSummary = {
        id: conversation.id,
        title: conversation.title,
        lastMessageTime: formatRelativeTime(now) // Format as "Just now" for the sidebar
      };
      
      // Set the new conversation to be passed to the sidebar
      setNewConversation(newConvSummary);
      
      // The refresh trigger is kept for backward compatibility with other code
      setConversationRefreshTrigger(prev => prev + 1);
      
      return conversation.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return undefined;
    }
  };

  // Handle deep research flow
  const handleDeepResearchFlow = async () => {
    const userInput = inputMessage;

    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: userInput
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsDeepResearchEnabled(false);
    setIsChatStarted(true);
    setIsLoading(true);

    try {
      const token = await getAccessToken();
      setAuthToken(token || '');

      // Create conversation if this is the first message (not in deep research mode yet)
      let conversationId = currentConversationId;
      if (!conversationId && !isInDeepResearch) {
        const newConversationId = await createConversationFromMessage(userInput);
        setCurrentConversationId(newConversationId);
        conversationId = newConversationId;
      }

      if (!isInDeepResearch) {
        // Start new deep research session
        const response = await fetch(`${api.defaults.baseURL}/api/deep-research/start`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ topic: userInput, conversation_id: conversationId, search_domains: searchDomains })
        });

        const data = await response.json();

        // Store session ID and enter deep research mode
        setDeepResearchSessionId(data.session_id);
        setIsInDeepResearch(true);

        // Display clarifying questions
        const questionsText = "I need to ask you a few clarifying questions to better understand what you're looking for:\n\n" +
          data.questions.map((q: string, i: number) => `${i + 1}. ${q}`).join('\n');

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: questionsText
        }]);

        setIsLoading(false);

      } else {
        // Submit answers to clarifying questions
        const response = await fetch(`${api.defaults.baseURL}/api/deep-research/submit-answers`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: deepResearchSessionId,
            answers: userInput,
            conversation_id: conversationId
          })
        });

        const data = await response.json();

        if (data.ready_for_research) {
          // Ready to generate report
          const processingMessage = "Thank you! I have all the information I need. Now I'll conduct deep research on your topic. This may take a few minutes...\n\n";

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: processingMessage
          }]);

          // Generate research report
          const reportResponse = await fetch(`${api.defaults.baseURL}/api/deep-research/generate-report`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ session_id: deepResearchSessionId, conversation_id: conversationId })
          });

          const reportData = await reportResponse.json();

          // Exit deep research mode
          setIsInDeepResearch(false);
          setDeepResearchSessionId(null);

          // Display the generated report with structured parts
          const reportContent = `Thank you! I have all the information I need. Now I'll conduct deep research on your topic. This may take a few minutes...\n\n## Research Report Complete\n\n${reportData.message}`;
          const reportParts: any[] = [
            { type: 'text', content: reportContent },
            { type: 'generated_files', files: [reportData.file] },
          ];

          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: reportContent,
              parts: reportParts,
            };
            return updated;
          });

          setIsLoading(false);

        } else {
          // More questions to answer
          const questionsText = "Thank you for those answers. I have a few more questions:\n\n" +
            data.questions.map((q: string, i: number) => `${i + 1}. ${q}`).join('\n');

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: questionsText
          }]);

          setIsLoading(false);
        }
      }

    } catch (error) {
      console.error('Error in deep research flow:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, there was an error with the deep research process. Please try again.'
      }]);
      setIsLoading(false);
      setIsInDeepResearch(false);
      setDeepResearchSessionId(null);
    }
  };

  // Handle sending a message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    // Require at least some text in the message (minimum length 1)
    if (!inputMessage.trim()) return;

    // Handle Agent conversation
    if (currentAgent) {
      await sendAgentMessage(inputMessage);
      return;
    }

    // Handle Deep Research flow
    if (isDeepResearchEnabled || isInDeepResearch) {
      await handleDeepResearchFlow();
      return;
    }

    // Use just the input message content
    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      ...(uploadedFiles.length > 0 && {
        files: uploadedFiles.map(f => ({
          name: f.name,
          content: f.content,
          type: f.type,
          size: f.size,
          file: f.file,
          download_url: f.download_url
        }))
      })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setUploadedFiles([]); // Clear files after sending
    if (webSearchMode === 'on') setWebSearchMode('off'); // Reset "Always" after sending; "Auto" persists
    setIsDeepResearchEnabled(false); // Reset deep research after sending
    setIsChatStarted(true);

    // Create initial assistant message placeholder and set loading immediately
    const assistantMessage: Message = {
      role: 'assistant',
      content: ''
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    try {
      // Create conversation if this is the first message
      let conversationId = currentConversationId;
      if (!conversationId && !isChatStarted) {
        const newConversationId = await createConversationFromMessage(inputMessage);
        setCurrentConversationId(newConversationId);
        conversationId = newConversationId;
      }

      // Get token first
      const token = await getAccessToken();
      setAuthToken(token || '');

      // Make streaming request
      const selectedModel = modelOptions[selectedModelIndex];
      const persona = localStorage.getItem('chatPersona') || 'default';

      const chatRequest: ChatRequest = {
        message: inputMessage,
        conversation_id: conversationId,
        llm_model_provider: selectedModel.provider,
        llm_model: selectedModel.model,
        llm_api_version: selectedModel.apiVersion,
        is_anthropic: selectedModel.isAnthropic,
        persona: persona !== 'default' ? persona : undefined
      };

      // Always send explicit web search mode
      chatRequest.web_search_mode = webSearchMode;
      chatRequest.search_domains = webSearchMode === 'on' ? searchDomains : [];
      chatRequest.sharepoint_enabled = isSharePointEnabled;
      chatRequest.sharepoint_sites = isSharePointEnabled ? sharePointSites.map((site) => site.url) : [];

      // Always send explicit image generation parameter
      chatRequest.force_image_generation = isImageGenerationEnabled;

      // Always send explicit deep research parameter
      chatRequest.force_deep_research = isDeepResearchEnabled;

      // Pass latest generated image blob name for editing previously generated images
      if (isImageGenerationEnabled && latestGeneratedImageBlobName) {
        chatRequest.latest_generated_image_blob_name = latestGeneratedImageBlobName;
      }

      // Pass application context for logging
      chatRequest.is_application = currentApplication !== null;
      chatRequest.application_id = currentApplication?.id;

      // Pass project context for memory + project files
      chatRequest.project_id = activeProjectId ?? undefined;

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append('request', JSON.stringify(chatRequest));

      // Add files if uploaded
      for (const f of uploadedFiles) {
        formData.append('files', f.file);
      }

      await streamChat(
        `${api.defaults.baseURL}/api/chat`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${await getAccessToken()}` },
          body: formData
        },
        {
          onUpdate: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
          },
          onFileMetadata: (meta) => {
            if (meta.files && meta.files.length > 0) {
              // Update user message with download URLs
              setMessages(prev => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].role === 'user' && updated[i].files && updated[i].files!.length > 0) {
                    updated[i].files = updated[i].files!.map((f, idx) => ({
                      ...f,
                      download_url: meta.files[idx]?.download_url ?? f.download_url
                    }));
                    break;
                  }
                }
                return updated;
              });
            }
          },
          onFileProcessing: (analyzing) => setIsAnalyzingFiles(analyzing),
          onImageGeneration: (data) => {
            if (data.images?.[0]?.blobName) {
              setLatestGeneratedImageBlobName(data.images[0].blobName);
            }
          },
          onError: (err) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: err.message };
              return updated;
            });
          },
          onDone: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
            setIsAnalyzingFiles(false);
            refreshQuota();
          }
        }
      );

      setIsLoading(false);

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, there was an error processing your request.' };
        return updated;
      });
      setIsLoading(false);
    }
  };

  // Send a message to an agent
  const sendAgentMessage = async (messageContent: string) => {
    if (!currentAgent) return;

    const userMessage: Message = {
      role: 'user',
      content: messageContent
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsChatStarted(true);

    const assistantMessage: Message = {
      role: 'assistant',
      content: ''
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    try {
      const token = await getAccessToken();
      setAuthToken(token || '');

      // Create conversation if this is the first message
      let conversationId = currentConversationId;
      if (!conversationId) {
        const newConversationId = await createConversationFromMessage(messageContent);
        setCurrentConversationId(newConversationId);
        conversationId = newConversationId;
      }

      await streamChat(
        `${api.defaults.baseURL}/api/agents/chat`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent_id: currentAgent.id, message: messageContent, conversation_id: conversationId })
        },
        {
          onUpdate: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
          },
          onFileProcessing: (analyzing) => setIsAnalyzingFiles(analyzing),
          onError: (err) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: err.message };
              return updated;
            });
          },
          onDone: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
            setIsAnalyzingFiles(false);
            refreshQuota();
          }
        }
      );

      setIsLoading(false);

    } catch (error) {
      console.error('Error sending agent message:', error);
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, there was an error processing your request.' };
        return updated;
      });
      setIsLoading(false);
    }
  };

  // Send a message directly without relying on inputMessage state.
  // `webSearchModeOverride` forces a web search mode for this one send (used by
  // the educational nudge's "Retry with web search" CTA), bypassing the current
  // (stale) webSearchMode state without mutating it.
  const sendMessageDirectly = async (messageContent: string, webSearchModeOverride?: 'auto' | 'on' | 'off') => {
    // Validate message content
    if (!messageContent.trim()) return;

    // Create user message
    const userMessage: Message = {
      role: 'user',
      content: messageContent,
      ...(uploadedFiles.length > 0 && {
        files: uploadedFiles.map(f => ({
          name: f.name,
          content: f.content,
          type: f.type,
          size: f.size,
          file: f.file,
          download_url: f.download_url
        }))
      })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setUploadedFiles([]);
    if (webSearchMode === 'on') setWebSearchMode('off'); // Reset "Always" after sending; "Auto" persists
    setIsChatStarted(true);

    // Create initial assistant message placeholder and set loading immediately
    const assistantMessage: Message = {
      role: 'assistant',
      content: ''
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    try {
      // Create conversation if this is the first message
      let conversationId = currentConversationId;
      if (!conversationId && !isChatStarted) {
        const newConversationId = await createConversationFromMessage(messageContent);
        setCurrentConversationId(newConversationId);
        conversationId = newConversationId;
      }

      // Get token first
      const token = await getAccessToken();
      setAuthToken(token || '');

      // Make streaming request
      const selectedModel = modelOptions[selectedModelIndex];
      const persona = localStorage.getItem('chatPersona') || 'default';

      const chatRequest: ChatRequest = {
        message: messageContent,
        conversation_id: conversationId,
        llm_model_provider: selectedModel.provider,
        llm_model: selectedModel.model,
        llm_api_version: selectedModel.apiVersion,
        is_anthropic: selectedModel.isAnthropic,
        persona: persona !== 'default' ? persona : undefined
      };

      // Always send explicit web search mode (override wins for one-shot retries)
      chatRequest.web_search_mode = webSearchModeOverride ?? webSearchMode;
      chatRequest.search_domains = chatRequest.web_search_mode === 'on' ? searchDomains : [];
      // A one-shot web search retry must not also carry SharePoint, which the API rejects.
      chatRequest.sharepoint_enabled = isSharePointEnabled && chatRequest.web_search_mode === 'off';
      chatRequest.sharepoint_sites = chatRequest.sharepoint_enabled ? sharePointSites.map((site) => site.url) : [];

      // Always send explicit image generation parameter
      chatRequest.force_image_generation = isImageGenerationEnabled;

      // Always send explicit deep research parameter
      chatRequest.force_deep_research = isDeepResearchEnabled;

      // Pass latest generated image blob name for editing previously generated images
      if (isImageGenerationEnabled && latestGeneratedImageBlobName) {
        chatRequest.latest_generated_image_blob_name = latestGeneratedImageBlobName;
      }

      // Pass application context for logging
      chatRequest.is_application = currentApplication !== null;
      chatRequest.application_id = currentApplication?.id;

      // Pass project context for memory + project files
      chatRequest.project_id = activeProjectId ?? undefined;

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append('request', JSON.stringify(chatRequest));

      // Add files if uploaded
      for (const f of uploadedFiles) {
        formData.append('files', f.file);
      }

      await streamChat(
        `${api.defaults.baseURL}/api/chat`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${await getAccessToken()}` },
          body: formData
        },
        {
          onUpdate: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
          },
          onFileMetadata: (meta) => {
            if (meta.files && meta.files.length > 0) {
              // Update user message with download URLs
              setMessages(prev => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].role === 'user' && updated[i].files && updated[i].files!.length > 0) {
                    updated[i].files = updated[i].files!.map((f, idx) => ({
                      ...f,
                      download_url: meta.files[idx]?.download_url ?? f.download_url
                    }));
                    break;
                  }
                }
                return updated;
              });
            }
          },
          onFileProcessing: (analyzing) => setIsAnalyzingFiles(analyzing),
          onImageGeneration: (data) => {
            if (data.images?.[0]?.blobName) {
              setLatestGeneratedImageBlobName(data.images[0].blobName);
            }
          },
          onError: (err) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: err.message };
              return updated;
            });
          },
          onDone: (snap: StreamSnapshot) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: snap.content, parts: snap.parts };
              return updated;
            });
            setIsAnalyzingFiles(false);
            refreshQuota();
          }
        }
      );

      setIsLoading(false);

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, there was an error processing your request.' };
        return updated;
      });
      setIsLoading(false);
    }
  };

  // ── Web search nudge CTA handlers ─────────────────────────────────
  // Retry the user's previous question with web search on.
  const handleNudgeRetryWithSearch = () => {
    // Find the last user message to resend.
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUserMessage) return;

    setWebSearchMode('on');
    sendMessageDirectly(lastUserMessage.content, 'on');
  };

  // Switch web search to Auto for subsequent messages.
  const handleNudgeEnableAuto = () => {
    setWebSearchMode('auto');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    // Auto-resize textarea
    e.target.style.height = isChatStarted ? '52px' : '56px';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleModelSelect = (index: number) => {
    setSelectedModelIndex(index);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const acceptedTypes = [
      'text/plain',
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ];

    // Validate all selected files are accepted types
    for (const file of selectedFiles) {
      if (!acceptedTypes.includes(file.type)) {
        alert('Unsupported file type. Please upload a TXT, CSV, Excel, PDF, Word, PowerPoint, PNG, or JPG file.');
        if (event.target) event.target.value = '';
        return;
      }
    }

    const imageFiles = selectedFiles.filter(f => f.type.startsWith('image/'));
    const nonImageFiles = selectedFiles.filter(f => !f.type.startsWith('image/'));

    // Don't allow mixing images and non-images in a single selection
    if (imageFiles.length > 0 && nonImageFiles.length > 0) {
      alert('Please upload either images or a document, not both at once.');
      if (event.target) event.target.value = '';
      return;
    }

    // Handle non-image files (multiple allowed)
    if (nonImageFiles.length > 0) {
      if (uploadedFiles.length > 0 && uploadedFiles[0].type.startsWith('image/')) {
        alert('Cannot add documents when images are already uploaded.');
        if (event.target) event.target.value = '';
        return;
      }
      const remainingSlots = MAX_IMAGE_FILES - uploadedFiles.length;
      const filesToAdd = nonImageFiles.slice(0, remainingSlots);
      for (const file of filesToAdd) {
        if (file.size > 200 * 1024 * 1024) {
          alert(`File ${file.name} exceeds the 200MB limit.`);
          if (event.target) event.target.value = '';
          return;
        }
      }
      setUploadedFiles(prev => [
        ...prev,
        ...filesToAdd.map(file => ({ name: file.name, type: file.type, size: file.size, file })),
      ]);
      setHasEverHadFile(true);
      if (event.target) event.target.value = '';
      return;
    }

    // Handle image files (up to MAX_IMAGE_FILES)
    // Can't add images if a non-image file is already uploaded
    if (uploadedFiles.length > 0 && !uploadedFiles[0].type.startsWith('image/')) {
      alert('Cannot add images when a document is already uploaded. Remove the document first.');
      if (event.target) event.target.value = '';
      return;
    }

    const remainingSlots = MAX_IMAGE_FILES - uploadedFiles.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${MAX_IMAGE_FILES} images allowed.`);
      if (event.target) event.target.value = '';
      return;
    }

    const filesToProcess = imageFiles.slice(0, remainingSlots);
    if (imageFiles.length > remainingSlots) {
      alert(`Only ${remainingSlots} more image(s) can be added. Maximum is ${MAX_IMAGE_FILES}.`);
    }

    for (const file of filesToProcess) {
      processImageFile(file);
    }

    if (event.target) event.target.value = '';
  };

  const handleDeleteFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Only image files can be pasted. Please paste a PNG or JPG image.');
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB per image
    if (file.size > maxSize) {
      alert('Image size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      if (arrayBuffer) {
        const uint8Array = new Uint8Array(arrayBuffer);
        let binaryString = '';
        for (let i = 0; i < uint8Array.byteLength; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const base64Content = btoa(binaryString);

        setUploadedFiles(prev => [...prev, {
          name: file.name || `pasted-image-${Date.now()}.${file.type.split('/')[1]}`,
          content: base64Content,
          type: file.type,
          size: file.size,
          file: file
        }]);
        setHasEverHadFile(true);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handlePaste = (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    // Check if the clipboard contains text alongside an image.
    // Apps like Excel put both text/plain and image/png on the clipboard;
    // in that case we should let the browser paste the text normally.
    let hasText = false;
    let imageItem: DataTransferItem | null = null;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'string' && (item.type === 'text/plain' || item.type === 'text/html')) {
        hasText = true;
      }
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        imageItem = item;
      }
    }

    // Only handle as image if there's no text alternative (pure image paste)
    if (imageItem && !hasText) {
      // Can't paste images if a non-image file is uploaded or at max capacity
      if (uploadedFiles.length > 0 && !uploadedFiles[0].type.startsWith('image/')) return;
      if (uploadedFiles.length >= MAX_IMAGE_FILES) return;

      event.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        processImageFile(file);
      }
    }
  };

  // Add paste event listener (use ref to avoid stale closure)
  const handlePasteRef = useRef(handlePaste);
  handlePasteRef.current = handlePaste;

  useEffect(() => {
    const listener = (e: ClipboardEvent) => handlePasteRef.current(e);
    document.addEventListener('paste', listener);
    return () => {
      document.removeEventListener('paste', listener);
    };
  }, []);



  // Handle conversation loading from router state (e.g., from Applications page)
  // Reset the newConversation state after it's been processed (a short delay)
  useEffect(() => {
    if (newConversation) {
      const timer = setTimeout(() => {
        setNewConversation(undefined);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [newConversation]);

  useEffect(() => {
    const state = location.state as {
      conversationId?: string;
      applicationData?: {
        id: string;
        title: string;
        customPrompt: string;
        defaultModelProvider?: string;
        defaultModel?: string;
        defaultModelApiVersion?: string;
      };
      agentData?: {
        id: string;
        title: string;
        description: string;
        img_url: string;
        required_connection?: string;
      };
    } | null;

    if (state?.conversationId) {
      // Load the specific conversation
      loadConversation(state.conversationId);
      // Clear the state to prevent reloading on subsequent renders
      window.history.replaceState({}, document.title);
    } else if (state?.applicationData) {
      // Set current application data
      setCurrentApplication(state.applicationData);
      setCurrentAgent(null);
      // Mark this as a new application conversation
      setIsNewApplicationConversation(true);

      // Set default model if provided and available
      if (state.applicationData.defaultModelProvider &&
          state.applicationData.defaultModel &&
          modelOptions.length > 0) {
        const matchingModelIndex = modelOptions.findIndex(model =>
          model.provider === state.applicationData?.defaultModelProvider &&
          model.model === state.applicationData?.defaultModel
        );

        if (matchingModelIndex !== -1) {
          setSelectedModelIndex(matchingModelIndex);
        }
      }

      // Start new conversation with application's custom prompt
      startNewConversation(state.applicationData.customPrompt);
      // Clear the state to prevent reloading on subsequent renders
      window.history.replaceState({}, document.title);
    } else if (state?.agentData) {
      // Set current agent data
      setCurrentAgent(state.agentData);
      setCurrentApplication(null);
      setIsNewApplicationConversation(false);
      // Start new conversation for agent (keepAgent=true to preserve agent state)
      startNewConversation(undefined, true);
      // Clear the state to prevent reloading on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, modelOptions]);

  // Check connection status whenever a connection-requiring agent becomes active.
  useEffect(() => {
    const provider = currentAgent?.required_connection;
    if (!provider) {
      setConnectionChecked(true);
      setIsConnected(null);
      return;
    }

    let cancelled = false;
    setConnectionChecked(false);
    getConnectionStatus(provider)
      .then((status) => {
        if (!cancelled) setIsConnected(status.connected);
      })
      .catch((err) => {
        console.error('Error checking connection status:', err);
        if (!cancelled) setIsConnected(false);
      })
      .finally(() => {
        if (!cancelled) setConnectionChecked(true);
      });

    return () => { cancelled = true; };
  }, [currentAgent]);

  // On return from the OAuth consent flow the browser lands on
  // /?agentId=...&connected=... (a full-page redirect wiped React state, so we
  // restore the agent from the query, then re-check connection status).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentId = params.get('agentId');
    const connected = params.get('connected');
    if (!agentId && !connected) return;

    const restore = async () => {
      if (agentId) {
        try {
          const agent = await getAgent(agentId);
          setCurrentAgent({
            id: agent.id,
            title: agent.title,
            description: agent.description,
            img_url: agent.img_url,
            required_connection: agent.required_connection
          });
          setCurrentApplication(null);
          setIsNewApplicationConversation(false);
          startNewConversation(undefined, true);
        } catch (err) {
          console.error('Error restoring agent after connect:', err);
        }
      }

      // Strip the OAuth params so a refresh doesn't re-trigger this.
      window.history.replaceState({}, document.title, window.location.pathname);
    };
    restore();
  }, []);

  const handleConnectAccount = async () => {
    const provider = currentAgent?.required_connection;
    if (!provider || !currentAgent) return;

    try {
      setIsConnecting(true);
      await getAccessToken();
      // Absolute frontend URL to come back to, carrying the agent id so we can
      // restore the chat context after the full-page OAuth redirect.
      const returnTo = `${window.location.origin}/?agentId=${encodeURIComponent(currentAgent.id)}`;
      const authorizeUrl = await getConnectionAuthorizeUrl(provider, returnTo);
      window.location.href = authorizeUrl;
    } catch (err) {
      console.error('Error starting connection flow:', err);
      setIsConnecting(false);
    }
  };

  // Handle auto-sending of application custom prompts
  useEffect(() => {
    if (autoSendMessage) {
      const messageToSend = autoSendMessage;
      setAutoSendMessage(null); // Clear the auto-send flag
      
      // Send the message directly
      setTimeout(() => {
        sendMessageDirectly(messageToSend);
      }, 100);
    }
  }, [autoSendMessage]);


  // Handle conversation selection from sidebar
  const handleSelectConversation = (conversationId: string) => {
    // Prevent selection if already loading a conversation
    if (isLoadingConversation) {
      return;
    }
    
    if (conversationId === '') {
      // Start new conversation
      startNewConversation();
    } else {
      // Load existing conversation
      loadConversation(conversationId);
    }
  };

  // Gate the chat when the active agent requires an external connection the user
  // has not yet made. While the status check is in flight we keep the gate closed.
  const needsConnectionGate = !!currentAgent?.required_connection && connectionChecked && isConnected === false;

  return (
    <>
      {/* Conversation Sidebar */}
      <ConversationSidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        onSelectConversation={handleSelectConversation}
        currentConversationId={currentConversationId}
        refreshTrigger={conversationRefreshTrigger}
        isLoadingConversation={isLoadingConversation}
        newConversation={newConversation}
      />

      {/* Header Component */}
      <Header currentAgent={currentAgent} />


      <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.background,
      padding: '0px',
      margin: '0px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
      overflow: 'hidden',
      marginLeft: isSidebarOpen ? '320px' : '60px',
      transition: 'margin-left 0.3s ease-in-out'
    }}>
      {/* Model Selector - hidden for agent conversations */}
      {!currentAgent && (
        <ModelSelector
          modelOptions={modelOptions}
          selectedModelIndex={selectedModelIndex}
          onModelSelect={handleModelSelect}
          onToggleConversations={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          isLoading={isLoadingModels}
          error={modelOptionsError}
          quota={quota}
        />
      )}

      {/* Chat container */}
      <div 
        id="chat-container"
        style={{
          flex: 1,
          overflow: isChatStarted ? 'auto' : 'hidden',
          padding: '20px',
          paddingTop: isChatStarted ? '80px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isChatStarted ? 'flex-start' : 'center',
          alignItems: isChatStarted ? 'stretch' : 'center',
          paddingBottom: isChatStarted ? '0' : '20px',
          backgroundImage: !isChatStarted ? 'radial-gradient(circle at 25% 25%, rgba(0, 120, 212, 0.03) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(200, 110, 250, 0.03) 0%, transparent 50%)' : 'none',
          minHeight: 0
        }}>
        {isLoadingConversation ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{
              color: colors.textMuted,
              fontSize: '14px',
              fontWeight: 500
            }}>Loading conversation...</div>
            <style>
              {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
          </div>
        ) : needsConnectionGate ? (
          <ConnectAccountGate
            agentTitle={currentAgent?.title || 'this agent'}
            agentImg={currentAgent?.img_url}
            isConnecting={isConnecting}
            onConnect={handleConnectAccount}
            colors={colors}
          />
        ) : !isChatStarted ? (
          <WelcomeScreen
            inputMessage={inputMessage}
            isLoading={isLoading}
            uploadedFiles={uploadedFiles}
            hasEverHadFile={hasEverHadFile}
            onInputChange={handleInputChange}
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            onDeleteFile={handleDeleteFile}
            webSearchMode={webSearchMode}
            isImageGenerationEnabled={isImageGenerationEnabled}
            isDeepResearchEnabled={isDeepResearchEnabled}
            onWebSearchModeChange={setWebSearchMode}
            onImageGenerationToggle={setIsImageGenerationEnabled}
            onDeepResearchToggle={setIsDeepResearchEnabled}
            searchDomains={searchDomains}
            onSearchDomainsChange={setSearchDomains}
            isSharePointEnabled={isSharePointEnabled}
            onSharePointToggle={setIsSharePointEnabled}
            sharePointSites={sharePointSites}
            onSharePointSitesChange={setSharePointSites}
            isAgentMode={!!currentAgent}
          />
        ) : (
          <ChatContainer
            messages={displayMessages}
            isLoading={isLoading}
            isAnalyzingFiles={isAnalyzingFiles}
            isSidebarOpen={isSidebarOpen}
            conversationId={currentConversationId}
            agentId={currentAgent?.id}
            modelProvider={modelOptions[selectedModelIndex]?.provider}
            modelName={modelOptions[selectedModelIndex]?.model}
            modelApiVersion={modelOptions[selectedModelIndex]?.apiVersion}
            onNudgeRetryWithSearch={handleNudgeRetryWithSearch}
            onNudgeEnableAuto={handleNudgeEnableAuto}
          />
        )}
      </div>

      {/* Input area (only shown when chat started) */}
      {isChatStarted && !needsConnectionGate && (
        <div style={{
          padding: '20px',
          backgroundColor: colors.background,
          flexShrink: 0
        }}>
          <ChatInput
            inputMessage={inputMessage}
            isLoading={isLoading}
            uploadedFiles={uploadedFiles}
            hasEverHadFile={hasEverHadFile}
            onInputChange={handleInputChange}
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            onDeleteFile={handleDeleteFile}
            webSearchMode={webSearchMode}
            isImageGenerationEnabled={isImageGenerationEnabled}
            isDeepResearchEnabled={isDeepResearchEnabled}
            onWebSearchModeChange={setWebSearchMode}
            onImageGenerationToggle={setIsImageGenerationEnabled}
            onDeepResearchToggle={setIsDeepResearchEnabled}
            searchDomains={searchDomains}
            onSearchDomainsChange={setSearchDomains}
            isSharePointEnabled={isSharePointEnabled}
            onSharePointToggle={setIsSharePointEnabled}
            sharePointSites={sharePointSites}
            onSharePointSitesChange={setSharePointSites}
            isChatStarted={isChatStarted}
            compact={true}
            isAgentMode={!!currentAgent}
          />
        </div>
      )}
      </div>
    </>
  );
};

export default HomePage;